import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageContext } from '@/i18n/LanguageContext';
import { pricingCourses } from '@/mocks/pricingData';

export default function PricingTable() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  return (
    <section className="relative py-20 md:py-28 bg-background-50">
      <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16">
        <div className="text-center mb-14">
          {String(t('pricing.table.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
          <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
            {t('pricing.table.title')}
          </h2>
          {String(t('pricing.table.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
          <div className="gold-line mx-auto"></div>
          {String(t('pricing.table.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed mt-6 max-w-[700px] mx-auto">{__s}</p>))}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-primary-900">
                <th className="p-5 text-left text-accent-400 text-[15px] tracking-[0.08em] font-medium border-r border-primary-800/50 w-[200px]">
                  {t('pricing.table.colItem')}
                </th>
                {pricingCourses.map((course, idx) => (
                  <th key={course.id} className="p-5 text-center text-accent-400 text-[15px] tracking-[0.08em] font-medium border-r border-primary-800/50 last:border-r-0">
                    {lang === 'en' ? t(`pricing.course.${idx}.name`) : course.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-background-200/60">
                <td className="p-5 text-[15px] text-foreground-600 tracking-[0.04em] bg-background-100/50">
                  {t('pricing.table.rowDuration')}
                </td>
                {pricingCourses.map((course, idx) => (
                  <td key={course.id} className="p-5 text-center text-[16px] text-foreground-800 tracking-[0.04em] border-l border-background-200/40 last:border-r-0">
                    {lang === 'en' ? t(`pricing.course.${idx}.duration`) : course.duration}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-background-200/60">
                <td className="p-5 text-[15px] text-foreground-600 tracking-[0.04em] bg-background-100/50">
                  {t('pricing.table.rowDays')}
                </td>
                {pricingCourses.map((course, idx) => (
                  <td key={course.id} className="p-5 text-center text-[16px] text-foreground-800 tracking-[0.04em] border-l border-background-200/40 last:border-r-0">
                    {lang === 'en' ? t(`pricing.course.${idx}.days`) : course.days}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-background-200/60">
                <td className="p-5 text-[15px] text-foreground-600 tracking-[0.04em] bg-background-100/50">
                  {t('pricing.table.rowHours')}
                </td>
                {pricingCourses.map((course, idx) => (
                  <td key={course.id} className="p-5 text-center text-[16px] text-foreground-800 tracking-[0.04em] border-l border-background-200/40 last:border-r-0">
                    {lang === 'en' ? t(`pricing.course.${idx}.hours`) : course.hours}
                  </td>
                ))}
              </tr>
              <tr className="border-b border-background-200/60">
                <td className="p-5 text-[15px] text-foreground-600 tracking-[0.04em] bg-background-100/50">
                  {t('pricing.table.rowSchedule')}
                </td>
                {pricingCourses.map((course, idx) => (
                  <td key={course.id} className="p-5 text-center text-[15px] text-foreground-700 tracking-[0.04em] border-l border-background-200/40 last:border-r-0">
                    {lang === 'en' ? t(`pricing.course.${idx}.schedule`) : course.schedule}
                  </td>
                ))}
              </tr>
              <tr>
                <td className="p-5 text-[15px] text-foreground-600 tracking-[0.04em] bg-background-100/50">
                  {t('pricing.table.rowPrice')}
                </td>
                {pricingCourses.map((course) => (
                  <td key={course.id} className="p-5 text-center border-l border-background-200/40 last:border-r-0">
                    <span className="text-[18px] text-accent-600 tracking-[0.06em] font-medium">
                      {course.price.toLocaleString()}{lang === 'en' ? ' JPY' : '円'}
                    </span>
                    <span className="block text-[13px] text-foreground-400 tracking-[0.04em] mt-1">
                      {t('pricing.table.taxIncluded')}
                    </span>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <div className="md:hidden space-y-4">
          {pricingCourses.map((course, idx) => (
            <div key={course.id} className="border border-background-200/60 bg-background-50 p-5">
              <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-1">
                {lang === 'en' ? t(`pricing.course.${idx}.name`) : course.name}
              </h3>
              <p className="text-[13px] text-accent-500 tracking-[0.1em] mb-4">
                {course.nameEn}
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-[15px]">
                  <span className="text-foreground-500 tracking-[0.04em]">{t('pricing.table.rowDuration')}</span>
                  <span className="text-foreground-800 tracking-[0.04em]">{lang === 'en' ? t(`pricing.course.${idx}.duration`) : course.duration}</span>
                </div>
                <div className="flex justify-between text-[15px]">
                  <span className="text-foreground-500 tracking-[0.04em]">{t('pricing.table.rowDays')}</span>
                  <span className="text-foreground-800 tracking-[0.04em]">{lang === 'en' ? t(`pricing.course.${idx}.days`) : course.days}</span>
                </div>
                <div className="flex justify-between text-[15px]">
                  <span className="text-foreground-500 tracking-[0.04em]">{t('pricing.table.rowHours')}</span>
                  <span className="text-foreground-800 tracking-[0.04em]">{lang === 'en' ? t(`pricing.course.${idx}.hours`) : course.hours}</span>
                </div>
                <div className="flex justify-between text-[15px]">
                  <span className="text-foreground-500 tracking-[0.04em]">{t('pricing.table.rowSchedule')}</span>
                  <span className="text-foreground-700 tracking-[0.04em]">{lang === 'en' ? t(`pricing.course.${idx}.schedule`) : course.schedule}</span>
                </div>
              </div>
              <div className="border-t border-background-200/60 pt-3 text-center">
                <span className="text-[18px] text-accent-600 tracking-[0.06em] font-medium">
                  {course.price.toLocaleString()}{lang === 'en' ? ' JPY' : '円'}
                </span>
                <span className="text-[13px] text-foreground-400 tracking-[0.04em] ml-1">
                  {t('pricing.table.taxIncluded')}
                </span>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-[13px] text-foreground-400 tracking-[0.04em] mt-5">
          {t('pricing.table.courseListNote')}
          <Link to="/courses" className="text-accent-600 hover:text-accent-500 underline underline-offset-2 transition-colors mx-1">
            {t('pricing.table.courseListLink')}
          </Link>
          {t('pricing.table.courseListNoteSuffix')}
        </p>
      </div>
    </section>
  );
}
